import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Thumb, DateRange, ArchivedToggle, TabBar, Row } from "@/components/list";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Upload, Loader2, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

const TABS = ["Not Started", "Partially Done", "Done"];
const tabOf = (b) => {
  const s = b.stage || "Not Started";
  if (s === "Not Started") return "Not Started";
  if (s === "In Edit") return "Partially Done";
  return "Done";
};

const AssignedBriefs = () => {
  const [briefs, setBriefs] = useState([]);
  const [tab, setTab] = useState("Not Started");
  const [archived, setArchived] = useState(false);
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [form, setForm] = useState({ title: "", brief_id: "", type: "Video", google_drive_link: "", image_path: "", internal_notes: "", mark: "Partially Done" });

  const load = useCallback(async () => {
    const params = { archived };
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    try { const b = await api.get("/briefs", { params }); setBriefs(b.data); }
    catch (e) { toast.error("Failed to load briefs"); }
  }, [archived, from, to]);
  useEffect(() => { load(); }, [load]);

  const briefById = (id) => briefs.find((b) => b.id === id);
  const openForBrief = (brief) => { setForm({ title: "", brief_id: brief.id, type: "Video", google_drive_link: "", image_path: "", internal_notes: "", mark: "Partially Done" }); setOpen(true); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/creatives/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: res.data.path })); toast.success("Image uploaded");
    } catch (e) { toast.error("Upload failed"); }
    setUploading(false);
  };

  const createCreative = async () => {
    if (!form.title || !form.brief_id) { toast.error("Add a title"); return; }
    const brief = briefById(form.brief_id);
    setSaving(true);
    try {
      await api.post("/creatives", {
        title: form.title, client_id: brief.client_id, batch_name: brief.batch_name,
        type: form.type, google_drive_link: form.google_drive_link, image_path: form.image_path || null,
        brief_id: form.brief_id, status: "Ready for Review", internal_notes: form.internal_notes,
      });
      await api.put(`/briefs/${form.brief_id}/stage`, { stage: form.mark === "Fully Done" ? "In Review" : "In Edit" });
      toast.success(`Creative added — brief marked ${form.mark}`);
      setOpen(false); load();
    } catch (e) { toast.error("Could not add creative"); }
    setSaving(false);
  };

  const setArch = async (id, val) => { await api.put(`/briefs/${id}`, { archived: val }); toast.success(val ? "Archived" : "Unarchived"); load(); };

  const counts = TABS.reduce((a, t) => { a[t] = briefs.filter((b) => tabOf(b) === t).length; return a; }, {});
  const filtered = briefs.filter((b) => tabOf(b) === tab);

  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Assigned Briefs</h1>
        <p className="text-zinc-400 mt-1">Your briefs. Add creatives and mark progress in the popup.</p></div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <TabBar tabs={TABS} value={tab} onChange={setTab} counts={counts} testidPrefix="brief-tab" />
        <div className="flex items-center gap-3">
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          <ArchivedToggle archived={archived} onToggle={() => setArchived((a) => !a)} />
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((b) => (
          <Row key={b.id} testid={`assigned-brief-${b.id}`}>
            <Thumb src={(b.thumbnail_path || b.client_thumbnail) ? fileUrl(b.thumbnail_path || b.client_thumbnail) : null} href={b.google_doc_link} alt={b.title} />
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{b.title}</p>
              <p className="text-zinc-400 text-sm truncate">{b.client_name} • {b.num_creatives || 0} creatives • Due {b.due_date || "—"}</p>
            </div>
            <StatusBadge status={b.stage || "Not Started"} />
            <Button data-testid={`add-creative-${b.id}`} onClick={() => openForBrief(b)} size="sm" className="rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold shrink-0"><Plus className="w-4 h-4 mr-1" /> Add creative</Button>
            <button data-testid={`archive-brief-${b.id}`} onClick={() => setArch(b.id, !archived)} className="text-zinc-500 hover:text-brand shrink-0" title={archived ? "Unarchive" : "Archive"}>
              {archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
            </button>
          </Row>
        ))}
        {filtered.length === 0 && <p className="text-zinc-500 py-8 text-center">No briefs here.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-ink-800 border-ink-700 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">New creative</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-zinc-300">Title</Label><Input data-testid="creative-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-ink-900 border-ink-700 mt-1.5" /></div>
            <div><Label className="text-zinc-300">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="creative-type-select" className="bg-ink-900 border-ink-700 mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-800 border-ink-700 text-white"><SelectItem value="Video">Video</SelectItem><SelectItem value="Static">Static</SelectItem></SelectContent>
              </Select></div>
            {form.type === "Video" ? (
              <div><Label className="text-zinc-300">Google Drive video link</Label><Input data-testid="creative-drive-input" value={form.google_drive_link} onChange={(e) => setForm({ ...form, google_drive_link: e.target.value })} className="bg-ink-900 border-ink-700 mt-1.5" placeholder="https://drive.google.com/..." /></div>
            ) : (
              <div><Label className="text-zinc-300">Upload static image</Label>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="creative-image-file" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="mt-1.5 w-full border-ink-700 bg-ink-900 hover:bg-ink-700">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}{form.image_path ? "Image uploaded ✓" : "Choose image"}</Button>
                {form.image_path && <img src={fileUrl(form.image_path)} alt="preview" className="mt-3 rounded-xl max-h-40 object-cover w-full" />}</div>
            )}
            <div><Label className="text-zinc-300">Internal notes</Label><Textarea data-testid="creative-notes-input" value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} className="bg-ink-900 border-ink-700 mt-1.5" /></div>
            <div><Label className="text-zinc-300">Mark brief as</Label>
              <Select value={form.mark} onValueChange={(v) => setForm({ ...form, mark: v })}>
                <SelectTrigger data-testid="brief-mark-select" className="bg-ink-900 border-ink-700 mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-800 border-ink-700 text-white">
                  <SelectItem value="Partially Done">Partially Done (→ In Edit)</SelectItem>
                  <SelectItem value="Fully Done">Fully Done (→ In Review)</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button data-testid="creative-submit-button" onClick={createCreative} disabled={saving} className="rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Add creative</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignedBriefs;
